import React, { useEffect, useState } from "react";
import { Button as UIButton } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  RefreshCw,
  Download,
  Settings,
  Eye,
  EyeOff,
  RotateCcw,
  Save,
  Edit,
  ChevronUp,
  ChevronDown,
  Menu,
} from "lucide-react";
// Grid layout removed - using simple mobile-friendly layout

// Context and widgets
import {
  DashboardLayoutProvider,
  useDashboardLayout,
} from "@/contexts/DashboardLayoutContext";
import OverviewMetricsWidget from "@/components/widgets/OverviewMetricsWidget";
import TrafficSummaryWidget from "@/components/widgets/TrafficSummaryWidget";
import TopSourceCategoriesWidget from "@/components/widgets/TopSourceCategoriesWidget";
import DeviceAnalyticsTableWidget from "@/components/widgets/DeviceAnalyticsTableWidget";
import TopLinksWidget from "@/components/widgets/TopLinksWidget";
import TopLocationsWidget from "@/components/widgets/TopLocationsWidget";
import SearchKeywordsWidget from "@/components/widgets/SearchKeywordsWidget";
import UserJourneysWidget from "@/components/widgets/UserJourneysWidget";
import SystemInfoWidget from "@/components/widgets/SystemInfoWidget";
import PopularPagesWidget from "@/components/widgets/PopularPagesWidget";
import RealTimeActivityWidget from "@/components/widgets/RealTimeActivityWidget";
import DashboardMetricsWidget from "@/components/widgets/DashboardMetricsWidget";
import PerformanceMetricsWidget from "@/components/widgets/PerformanceMetricsWidget";
import PeakHoursWidget from "@/components/widgets/PeakHoursWidget";
import ConversionFunnelWidget from "@/components/widgets/ConversionFunnelWidget";

// Chart components (these will be wrapped in widgets)
import { TrafficSourceChart } from "@/components/TrafficSourceChart";
import { LocationChart } from "@/components/LocationChart";

// ResponsiveGridLayout removed - using simple mobile-friendly layout

interface AnalyticsStats {
  totalSessions: number;
  totalEvents: number;
  totalPageViews: number;
  uniqueVisitors: number;
  sessionsToday: number;
  sessionsThisWeek: number;
  sessionsThisMonth: number;
  popularPages: Array<{ page: string; views: number }>;
  deviceStats: {
    types: Record<string, number>;
    os: Record<string, number>;
    browsers: Record<string, number>;
  };
  locationStats: Array<{ location: string; count: number }>;
  trafficSources: {
    utm: Record<string, number>;
    searchEngines: Record<string, number>;
    referrers: Record<string, number>;
  };
  locationsBySource: Record<string, Record<string, number>>;
  searchKeywords: Array<{ keyword: string; count: number }>;
  deviceBreakdownBySource: Record<
    string,
    Array<{ type: string; os: string; count: number }>
  >;
  topDevicesBySource: Record<
    string,
    { type: string; os: string; count: number; percentage: number }
  >;
  userJourneys: Array<{
    sessionId: string;
    pageViews: string[];
    startTime: number;
    duration: number;
    device: string;
    location: string;
  }>;
  conversions: any[];
  searches: any[];
  errors: any[];
  realTimeVisitors: number;
}

const DashboardContent: React.FC = () => {
  const [stats, setStats] = useState<AnalyticsStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const {
    layout,
    updateLayout,
    toggleWidget,
    resetLayout,
    isCustomizing,
    setIsCustomizing,
  } = useDashboardLayout();

  const loadAnalytics = async () => {
    setLoading(true);
    try {
      const response = await fetch("/api/analytics");
      const data = await response.json();
      setStats(data);
      setLastUpdated(new Date());
    } catch (error) {
      console.error("Failed to load analytics:", error);
    }
    setLoading(false);
  };

  useEffect(() => {
    loadAnalytics();
    // Auto-refresh every 30 seconds
    const interval = setInterval(loadAnalytics, 30000);
    return () => clearInterval(interval);
  }, []);

  const exportData = async () => {
    try {
      const response = await fetch("/api/analytics");
      const data = await response.json();
      const dataStr = JSON.stringify(data, null, 2);
      const dataBlob = new Blob([dataStr], { type: "application/json" });
      const url = URL.createObjectURL(dataBlob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `tigola-analytics-${new Date().toISOString().split("T")[0]}.json`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Failed to export data:", error);
    }
  };

  const moveWidget = (widgetId: string, direction: "up" | "down") => {
    const visibleWidgets = layout.widgets
      .filter((w) => w.visible)
      .sort((a, b) => a.layout.y - b.layout.y);
    const currentIndex = visibleWidgets.findIndex((w) => w.id === widgetId);

    if (direction === "up" && currentIndex > 0) {
      // Swap Y positions with the widget above
      const currentWidget = visibleWidgets[currentIndex];
      const targetWidget = visibleWidgets[currentIndex - 1];

      const updatedWidgets = layout.widgets.map((widget) => {
        if (widget.id === currentWidget.id) {
          return {
            ...widget,
            layout: { ...widget.layout, y: targetWidget.layout.y },
          };
        }
        if (widget.id === targetWidget.id) {
          return {
            ...widget,
            layout: { ...widget.layout, y: currentWidget.layout.y },
          };
        }
        return widget;
      });

      updateLayout(updatedWidgets);
    } else if (
      direction === "down" &&
      currentIndex < visibleWidgets.length - 1
    ) {
      // Swap Y positions with the widget below
      const currentWidget = visibleWidgets[currentIndex];
      const targetWidget = visibleWidgets[currentIndex + 1];

      const updatedWidgets = layout.widgets.map((widget) => {
        if (widget.id === currentWidget.id) {
          return {
            ...widget,
            layout: { ...widget.layout, y: targetWidget.layout.y },
          };
        }
        if (widget.id === targetWidget.id) {
          return {
            ...widget,
            layout: { ...widget.layout, y: currentWidget.layout.y },
          };
        }
        return widget;
      });

      updateLayout(updatedWidgets);
    }
  };

  // handleLayoutChange removed - using simple up/down reordering

  const renderWidget = (widgetId: string) => {
    switch (widgetId) {
      case "dashboard-metrics":
        return <DashboardMetricsWidget stats={stats} />;
      case "overview-metrics":
        return <OverviewMetricsWidget stats={stats} />;
      case "traffic-summary":
        return <TrafficSummaryWidget stats={stats} />;
      case "top-source-categories":
        return <TopSourceCategoriesWidget stats={stats} />;
      case "device-analytics-table":
        return <DeviceAnalyticsTableWidget stats={stats} />;
      case "top-links":
        return <TopLinksWidget stats={stats} />;
      case "top-locations":
        return <TopLocationsWidget stats={stats} />;
      case "search-keywords":
        return <SearchKeywordsWidget stats={stats} />;
      case "traffic-sources-chart":
        return (
          <Card className="h-full">
            <CardHeader>
              <CardTitle>Traffic Sources Chart</CardTitle>
            </CardHeader>
            <CardContent className="h-[calc(100%-4rem)]">
              <TrafficSourceChart
                utmSources={stats?.trafficSources.utm || {}}
                searchEngines={stats?.trafficSources.searchEngines || {}}
                referrers={stats?.trafficSources.referrers || {}}
                totalVisitors={stats?.totalSessions || 1}
              />
            </CardContent>
          </Card>
        );
      case "location-chart":
        return (
          <Card className="h-full">
            <CardHeader>
              <CardTitle>Location Analytics</CardTitle>
            </CardHeader>
            <CardContent className="h-[calc(100%-4rem)]">
              <LocationChart
                locationStats={stats?.locationStats || []}
                locationsBySource={stats?.locationsBySource}
              />
            </CardContent>
          </Card>
        );
      case "user-journeys":
        return <UserJourneysWidget stats={stats} />;
      case "system-info":
        return <SystemInfoWidget stats={stats} />;
      case "popular-pages":
        return <PopularPagesWidget stats={stats} />;
      case "real-time-activity":
        return <RealTimeActivityWidget stats={stats} />;
      case "performance-metrics":
        return <PerformanceMetricsWidget stats={stats} />;
      case "peak-hours":
        return <PeakHoursWidget stats={stats} />;
      case "conversion-funnel":
        return <ConversionFunnelWidget stats={stats} />;
      default:
        return <div>Widget not found</div>;
    }
  };

  if (loading && !stats) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="h-8 w-8 animate-spin text-primary mx-auto mb-4" />
          <p className="text-gray-600">Loading customizable analytics...</p>
        </div>
      </div>
    );
  }

  // gridLayout removed - using simple mobile-friendly layout

  return (
    <div className="min-h-screen bg-gray-50 p-2 sm:p-4 lg:p-6 dashboard-container">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6 sm:mb-8">
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
            <div className="flex-1">
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">
                Analytics Dashboard
              </h1>
              <p className="text-sm sm:text-base text-gray-600">
                Mobile-friendly • Reorder widgets • Hide/show widgets
              </p>
              {lastUpdated && (
                <p className="text-xs sm:text-sm text-gray-500 mt-1">
                  Updated: {lastUpdated.toLocaleTimeString()}
                </p>
              )}
            </div>
            <div className="flex flex-wrap gap-2 sm:gap-3">
              <UIButton
                onClick={() => setIsCustomizing(!isCustomizing)}
                variant={isCustomizing ? "default" : "outline"}
                size="sm"
                className="flex items-center gap-1 sm:gap-2"
              >
                {isCustomizing ? (
                  <>
                    <Save className="h-4 w-4" />
                    <span className="hidden sm:inline">Save Layout</span>
                    <span className="sm:hidden">Save</span>
                  </>
                ) : (
                  <>
                    <Edit className="h-4 w-4" />
                    <span className="hidden sm:inline">Customize</span>
                    <span className="sm:hidden">Edit</span>
                  </>
                )}
              </UIButton>
              {isCustomizing && (
                <UIButton
                  onClick={resetLayout}
                  variant="outline"
                  size="sm"
                  className="flex items-center gap-1 sm:gap-2"
                >
                  <RotateCcw className="h-4 w-4" />
                  <span className="hidden sm:inline">Reset</span>
                </UIButton>
              )}
              <UIButton
                onClick={loadAnalytics}
                variant="outline"
                size="sm"
                disabled={loading}
                className="flex items-center gap-1 sm:gap-2"
              >
                <RefreshCw
                  className={`h-4 w-4 ${loading ? "animate-spin" : ""}`}
                />
                <span className="hidden sm:inline">Refresh</span>
              </UIButton>
              <UIButton
                onClick={exportData}
                variant="outline"
                size="sm"
                className="flex items-center gap-1 sm:gap-2"
              >
                <Download className="h-4 w-4" />
                <span className="hidden sm:inline">Export</span>
              </UIButton>
            </div>
          </div>
        </div>

        {/* Widget Controls */}
        {isCustomizing && (
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
                <Menu className="h-5 w-5" />
                Widget Controls
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <p className="text-sm text-gray-600 mb-4">
                  Toggle visibility and reorder widgets. Changes are saved
                  automatically.
                </p>
                <div className="space-y-2">
                  {layout.widgets
                    .sort((a, b) => a.layout.y - b.layout.y)
                    .map((widget, index) => (
                      <div
                        key={widget.id}
                        className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg"
                      >
                        {/* Reorder buttons */}
                        <div className="flex flex-col gap-1">
                          <UIButton
                            onClick={() => moveWidget(widget.id, "up")}
                            variant="outline"
                            size="sm"
                            disabled={index === 0}
                            className="h-6 w-6 p-0"
                            title="Move up"
                          >
                            <ChevronUp className="h-3 w-3" />
                          </UIButton>
                          <UIButton
                            onClick={() => moveWidget(widget.id, "down")}
                            variant="outline"
                            size="sm"
                            disabled={index === layout.widgets.length - 1}
                            className="h-6 w-6 p-0"
                            title="Move down"
                          >
                            <ChevronDown className="h-3 w-3" />
                          </UIButton>
                        </div>

                        {/* Visibility toggle */}
                        <UIButton
                          onClick={() => toggleWidget(widget.id)}
                          variant={widget.visible ? "default" : "outline"}
                          size="sm"
                          className="flex items-center gap-2 flex-1 justify-start"
                        >
                          {widget.visible ? (
                            <Eye className="h-4 w-4" />
                          ) : (
                            <EyeOff className="h-4 w-4" />
                          )}
                          <span className="text-sm truncate">
                            {widget.title}
                          </span>
                        </UIButton>
                      </div>
                    ))}
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Customizable Grid Layout */}
        {/* Mobile-friendly widget layout */}
        <div className="space-y-6">
          {layout.widgets
            .filter((widget) => widget.visible)
            .sort((a, b) => a.layout.y - b.layout.y) // Sort by Y position
            .map((widget) => (
              <div key={widget.id} className="relative">
                {/* Widget reorder controls (only shown when customizing) */}
                {isCustomizing && (
                  <div className="absolute top-2 right-2 z-10 flex gap-1">
                    <UIButton
                      size="sm"
                      variant="outline"
                      className="h-8 w-8 p-0 bg-white shadow-md"
                      onClick={() => moveWidget(widget.id, "up")}
                      title="Move up"
                    >
                      <ChevronUp className="h-4 w-4" />
                    </UIButton>
                    <UIButton
                      size="sm"
                      variant="outline"
                      className="h-8 w-8 p-0 bg-white shadow-md"
                      onClick={() => moveWidget(widget.id, "down")}
                      title="Move down"
                    >
                      <ChevronDown className="h-4 w-4" />
                    </UIButton>
                  </div>
                )}
                {renderWidget(widget.id)}
              </div>
            ))}
        </div>
      </div>
    </div>
  );
};

export default function CustomizableDashboard() {
  return (
    <DashboardLayoutProvider>
      <DashboardContent />
    </DashboardLayoutProvider>
  );
}
